import {
  Box,
  Button,
  CardMedia,
  Menu,
  MenuItem,
  styled,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useState, useCallback, useMemo, MouseEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getQueryKey } from '@trpc/react-query';
import axios from 'axios';
import find from 'lodash/find';

import { useDropzone, FileRejection } from 'react-dropzone';
import { SignedUrl } from 'src/utils/google/storage';

import LinearProgress, {
  linearProgressClasses,
} from '@mui/material/LinearProgress';

import Carousel from 'react-material-ui-carousel';
import DeleteIcon from '@mui/icons-material/Delete';
import { trpc } from 'src/utils/trpc';
import { inferProcedureOutput } from '@trpc/server';
import { AppRouter } from 'src/server/trpc/router';
import { ConfirmDialog } from '../components/form/ConfirmDialog';
import { DotsVertical } from 'mdi-material-ui';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import ImageIcon from '@mui/icons-material/Image';
import { Thumbnails } from './Thumbnails';
import CircleIcon from '@mui/icons-material/Circle';
import { window_open } from 'src/utils/flutter';
import { Alert, Snackbar } from '@mui/material';

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 10,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor:
      theme.palette.grey[theme.palette.mode === 'light' ? 200 : 800],
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: theme.palette.mode === 'light' ? '#1a90ff' : '#308fe8',
  },
}));

export type UploadToStorageProps = {
  files: Array<File>;
  onUploadProgress: ((progressEvent: any) => void) | undefined;
  path: string;
  bucketName: string;
};

export const uploadToStorage = async ({
  files,
  onUploadProgress,
  path,
  bucketName,
}: UploadToStorageProps) => {
  const mappedFiles = files.map((file) => ({
    filename: file.name,
    bucket: bucketName,
    path,
    content_type: file.type,
  }));

  const response = await axios.post('/api/google/storage/signed-urls', {
    files: mappedFiles,
  });

  const signedUrlResults = response.data;
  const signedUrls = signedUrlResults.signedUrls as Array<SignedUrl>;

  await Promise.all(
    signedUrls.map(async (signedUrl) => {
      const file = find(files, (file) => file.name === signedUrl.originalname);
      return await axios.put(signedUrl.signed_url, file, {
        headers: { 'Content-Type': signedUrl.content_type },
        onUploadProgress,
      });
    })
  );

  const fileNames = signedUrls.map((signedUrl) => signedUrl);

  return fileNames;
};

type DropzoneProps = {
  addImage: (files: Array<SignedUrl>) => void;
  allowedFiles: any;
};

type Message = {
  open: boolean;
  message: string;
  type?: 'success' | 'error';
};

const Dropzone: React.FC<DropzoneProps> = ({ addImage, allowedFiles }) => {
  const theme = useTheme();
  const [percentCompleted, setPercentCompleted] = useState(0);
  const [isUploadCompleted, setIsUploadCompleted] = useState(false);

  const [openMessage, setOpenMessage] = useState<Message>({
    open: false,
    message: '',
    type: 'success',
  });

  const onUploadProgress = (progressEvent: any) => {
    const percent = Math.round(
      (progressEvent.loaded * 100) / progressEvent.total
    );
    setPercentCompleted(percent);
  };

  const onDrop = useCallback(
    async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (fileRejections.length > 0) {
        setOpenMessage({
          open: true,
          message: `Removed ${fileRejections.length} invalid file formats.`,
          type: 'error',
        });
      }
      setPercentCompleted(0);
      setIsUploadCompleted(false);
      const results = await uploadToStorage({
        files: acceptedFiles,
        onUploadProgress,
        path: 'attachments',
        bucketName: 'carketa-next-attachments',
      });
      addImage(results);
      setIsUploadCompleted(true);
      // eslint-disable-next-line
    },
    // eslint-disable-next-line
    []
  );

  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } =
    useDropzone({
      onDrop,
      accept: allowedFiles
        ? allowedFiles
        : {
            'image/jpeg': [],
            'image/png': [],
            'text/csv': [],
            'application/msword': [],
            'application/pdf': [],
            'application/json': [],
          },
    });

  const baseStyle = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '20px',
    borderWidth: 2,
    borderRadius: 2,
    borderColor: '#c7c9cb',
    borderStyle: 'dashed',
    backgroundColor: '#8A8D93',
    color: theme.palette.primary.contrastText,
    outline: 'none',
    transition: 'border .24s ease-in-out',
  };

  const focusedStyle = {
    borderColor: '#2196f3',
  };

  const acceptStyle = {
    borderColor: '#00e676',
  };

  const rejectStyle = {
    borderColor: '#ff1744',
  };

  const style: any = useMemo(
    () => ({
      ...baseStyle,
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    // eslint-disable-next-line
    [isFocused, isDragAccept, isDragReject]
  );

  const handleCloseMessage = () => {
    setOpenMessage({
      open: false,
      message: '',
    });
  };

  const extensions = allowedFiles
    ? Object.keys(allowedFiles).map((key) => key.toUpperCase().split('/').pop())
    : null;

  return (
    <>
      <Snackbar
        open={openMessage.open}
        onClose={handleCloseMessage}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert variant="filled" elevation={3} severity={openMessage.type}>
          {openMessage.message}
        </Alert>
      </Snackbar>
      <Box sx={{ py: 2 }}>
        <div {...getRootProps({ style })}>
          <input {...getInputProps()} />
          <Box
            sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'center' }}
          >
            <p>
              Drag &apos;n&apos; drop some files here, or click to select files{' '}
            </p>
            {extensions && <p>({extensions.join(', ')})</p>}
          </Box>
          <Box
            sx={{ display: { xs: 'block', sm: 'none' }, textAlign: 'center' }}
          >
            <p>Click here to select file or access camera</p>
            {extensions && <p>({extensions.join(', ')})</p>}
          </Box>
        </div>
      </Box>
      {isUploadCompleted ? null : (
        <BorderLinearProgress variant="determinate" value={percentCompleted} />
      )}
    </>
  );
};

export type CloudFile = {
  bucket: string;
  content_type: string;
  ext: string;
  file: string;
  filename: string;
  test: string;
  originalname: string;
};

interface Props {
  id: string;
  setMainUrlEnabled?: boolean;
  showThumbnails: boolean;
  allowUpload: boolean;
  showPlaceholder: boolean;
  showMenu: boolean;
  pdfMode?: boolean;
  allowedFiles?: any;
}
export type Attachments = inferProcedureOutput<
  AppRouter['attachments']['getByKey']
>;

export const FileViewer: React.FC<Props> = ({
  id,
  setMainUrlEnabled,
  showThumbnails,
  allowUpload,
  showMenu,
  showPlaceholder = true,
  pdfMode = false,
  allowedFiles = null,
}) => {
  const [selected, setSelected] = useState({} as any);
  const [openDelete, setOpenDelete] = useState(false);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const rowOptionsOpen = Boolean(anchorEl);

  const queryClient = useQueryClient();
  const updateVehicleCard = getQueryKey(trpc.vehicles.getById);

  const { data: attachments, refetch } = trpc.attachments.getByKey.useQuery(
    { key: id },
    { keepPreviousData: true, refetchOnWindowFocus: false, enabled: !!id }
  );

  const handleRowOptionsClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleRowOptionsClose = () => {
    setAnchorEl(null);
  };

  const createAttachmentMutation = trpc.attachments.create.useMutation({
    onError(error) {
      console.error(error);
    },
    onSuccess() {
      queryClient.invalidateQueries(updateVehicleCard);
      refetch();
    },
  });

  const updateVehicleImageMutation = trpc.vehicles.update.useMutation({
    onError(error) {
      console.error(error);
    },
    onSuccess() {
      queryClient.invalidateQueries(updateVehicleCard);
    },
  });

  const deletePlaceholderMutation = trpc.vehicles.deleteAppraisalImage.useMutation({
    onError(error) {
      console.error(error);
    },
    onSuccess() {
      queryClient.invalidateQueries(updateVehicleCard);
      refetch();
    },
  });

  const onAddImage = (files: Array<SignedUrl>) => {
    files.forEach((file) => {
      createAttachmentMutation.mutate({ key: id, attachment: file });
    });
  };

  const updateAttachmentMutation = trpc.attachments.update.useMutation({
    onError(error) {
      console.error(error);
    },
    onSuccess() {
      refetch();
    },
  });

  const onImageDelete = () => {
    if (attachments) {
      updateAttachmentMutation.mutate({ id: selected.id });
      deletePlaceholderMutation.mutate({
      id: id,
      url: selected.payload.attachment.file,
    });
      setSelected(attachments[0]);
      handleRowOptionsClose();
    }
  };

  const downloadFile = () => {
    window_open(selected.payload.attachment.file, '_blank', 'noreferrer');
    handleRowOptionsClose();
  };

  const setDefaultPicture = () => {
    updateVehicleImageMutation.mutate({
      id: id,
      url: selected.payload.attachment.file,
    });
    handleRowOptionsClose();
  };

  const firstFile = attachments
    ? (attachments[0]?.payload as { attachment: CloudFile })
    : null;
  return (
    <Box>
      {attachments && attachments.length > 0 ? (
        <>
          {pdfMode ? (
            <>
              <div className="flex justify-center items-center flex-col mb-6  xs sm md">
                <CardMedia
                  className="Media"
                  image={firstFile?.attachment?.file || ''}
                  title={firstFile?.attachment?.originalname || ''}
                  sx={{
                    height: { xs: '400px', sm: '400px', md: '400px' },
                    width: { xs: '600px', sm: '600px', md: '600px' },
                    backgroundSize: {
                      xs: '375px auto',
                      sm: '400px auto',
                      md: '600px auto',
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      padding: 2,
                      zIndex: 12,
                      width: { xs: '75%', sm: '95%' },
                    }}
                  ></Box>
                </CardMedia>
              </div>
            </>
          ) : (
            <Carousel
              autoPlay={false}
              index={attachments
                .map(function (x) {
                  return x.id;
                })
                .indexOf(selected.id)}
              navButtonsAlwaysVisible={true}
              swipe={true}
              navButtonsWrapperProps={{
                style: { height: '50%', marginTop: '15%', bottom: '0' },
              }}
              changeOnFirstRender={true}
              onChange={(now: any) => setSelected(attachments[now])}
              IndicatorIcon={<CircleIcon sx={{ fontSize: '10px' }} />}
              indicatorIconButtonProps={{
                style: {
                  padding: '0px',
                },
              }}
              activeIndicatorIconButtonProps={{
                style: {
                  fontSize: '5px',
                },
              }}
              indicatorContainerProps={{
                style: {
                  textAlign: 'center',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  margin: '10px',
                  width: '95%',
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                },
              }}
            >
              {attachments.map((attachment, i) => {
                const payload = attachment?.payload as {
                  attachment: CloudFile;
                };
                return payload.attachment.ext !== 'jpeg' &&
                  payload.attachment.ext !== 'png' &&
                  payload.attachment.ext !== 'jpg' &&
                  payload.attachment.ext !== 'gif' ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        width: { xs: '50%', sm: '100%' },
                        position: 'absolute',
                        top: 0,
                      }}
                    >
                      <Button
                        variant="contained"
                        color="secondary"
                        size="small"
                        sx={{
                          display: showMenu ? '' : 'none',
                        }}
                        onClick={handleRowOptionsClick}
                      >
                        <DotsVertical />
                      </Button>
                    </Box>
                    <span
                      className={`fiv-viv fiv-icon-${payload.attachment.ext} fiv-size-lg`}
                      style={{ fontSize: '150px' }}
                    ></span>
                    <Typography
                      sx={{
                        marginTop: 1,
                        position: 'absolute',
                        bottom: 0,
                      }}
                    >
                      {payload.attachment.originalname}
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    key={i}
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: { xs: '300px', sm: '315px', md: '400px' },
                      flexDirection: 'column',
                    }}
                  >
                    <CardMedia
                      className="Media"
                      image={payload.attachment.file || ''}
                      title={payload.attachment.originalname || ''}
                      sx={{
                        height: { xs: '400px', sm: '400px', md: '400px' },
                        width: { xs: '600px', sm: '600px', md: '600px' },
                        backgroundSize: {
                          xs: '375px auto',
                          sm: '400px auto',
                          md: '600px auto',
                        },
                      }}
                      key={i}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          padding: 2,
                          zIndex: 12,
                          width: { xs: '75%', sm: '95%' },
                        }}
                      >
                        <Button
                          variant="contained"
                          color="secondary"
                          size="small"
                          sx={{
                            display: showMenu ? '' : 'none',
                          }}
                          onClick={handleRowOptionsClick}
                        >
                          <DotsVertical />
                        </Button>
                      </Box>
                    </CardMedia>
                  </Box>
                );
              })}
            </Carousel>
          )}
        </>
      ) : showPlaceholder ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '400px',
            flexDirection: 'column',
          }}
        >
          <CardMedia
            id="file-viewer"
            className="Media"
            image={'/img/car_new_arrival.jpg'}
            title={'/img/car_new_arrival.jpg'}
            sx={{
              height: '400px',
              width: '400px',
              backgroundSize: 'contain',
            }}
          ></CardMedia>
        </Box>
      ) : (
        <></>
      )}
      {showThumbnails ? (
        <Thumbnails
          selected={selected}
          onThumbnailSelect={(attachment) => {
            setSelected(attachment);
          }}
          attachments={attachments || []}
        />
      ) : null}

      {allowUpload ? (
        <Box
          sx={{
            py: 2,
          }}
        >
          <Dropzone addImage={onAddImage} allowedFiles={allowedFiles} />
        </Box>
      ) : null}
      {selected && selected.payload ? (
        <Menu
          keepMounted
          anchorEl={anchorEl}
          open={rowOptionsOpen}
          onClose={handleRowOptionsClose}
          sx={{
            display: showMenu ? '' : 'none',
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{ style: { minWidth: '8rem' } }}
        >
          <MenuItem onClick={() => setOpenDelete(true)}>
            <DeleteIcon
              sx={{
                marginRight: '10px',
                fill: (theme) => (theme.palette.mode === 'dark' ? 'white' : ''),
              }}
            />
            Delete
          </MenuItem>
          <MenuItem onClick={() => downloadFile()}>
            <CloudDownloadIcon
              sx={{
                marginRight: '10px',
                fill: (theme) => (theme.palette.mode === 'dark' ? 'white' : ''),
              }}
            />
            Download File
          </MenuItem>
          <MenuItem
            sx={{
              display:
                setMainUrlEnabled &&
                selected &&
                (selected.payload.attachment?.ext === 'jpg' ||
                  selected.payload.attachment?.ext === 'jpeg' ||
                  selected.payload.attachment?.ext === 'png' ||
                  selected.payload.attachment?.ext === 'gif')
                  ? ''
                  : 'none',
            }}
            onClick={() => setDefaultPicture()}
          >
            <ImageIcon
              sx={{
                marginRight: '10px',
                fill: (theme) => (theme.palette.mode === 'dark' ? 'white' : ''),
              }}
            />
            Set Default Picture
          </MenuItem>
        </Menu>
      ) : null}
      {openDelete && (
        <ConfirmDialog
          title="Remove?"
          description={`Are you sure you want to remove this file?`}
          open={openDelete}
          handleClose={() => setOpenDelete(false)}
          handleOk={() => onImageDelete()}
        />
      )}
    </Box>
  );
};
